import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasePage from '../../components/Layout/BasePage';
import TabBar from '../../components/Layout/TabBar';
import { renderTabLabel } from '../../components/Layout/tabLabel';
import useUrlTab from '../../hooks/useUrlTab';
import Users from './Users';
import Departments from './Departments';
import OperatorSchedules from './OperatorSchedules';
import AdminOverview from './AdminOverview';
import Skills from './Skills';
import Roles from './Roles';
import Colors from './Colors';
import Countries from './Countries';
import TaskTypes from './TaskTypes';

function AdminPage() {
  const { t } = useTranslation('admin');
  // Active tab in the URL (?tab=), remembered per module
  const { activeTab, setActiveTab } = useUrlTab('admin', [
    'overview', 'users-roles', 'departments', 'roles', 'schedules', 'skills', 'colors', 'countries', 'taskTypes'
  ], 'overview');
  const [schedulesEditMode, setSchedulesEditMode] = useState(false);
  const [schedulesToolbar, setSchedulesToolbar] = useState(null);
  const [departmentsToolbar, setDepartmentsToolbar] = useState(null);
  const [skillsToolbar, setSkillsToolbar] = useState(null);
  const [rolesToolbar, setRolesToolbar] = useState(null);
  const [colorsToolbar, setColorsToolbar] = useState(null);
  const [countriesToolbar, setCountriesToolbar] = useState(null);
  const [taskTypesToolbar, setTaskTypesToolbar] = useState(null);

  const tabs = [
    {
      id: 'overview',
      label: renderTabLabel(t('tabs.overview'), 'o'),
      shortcut: 'o',
      hasSubTabs: false
    },
    {
      id: 'users-roles',
      label: renderTabLabel(t('tabs.users'), 'u'),
      shortcut: 'u',
      hasSubTabs: false
    },
    {
      id: 'departments',
      label: renderTabLabel(t('tabs.departments'), 'd'),
      shortcut: 'd',
      hasSubTabs: false
    },
    {
      id: 'roles',
      label: renderTabLabel(t('tabs.roles', 'Roles'), 'r'),
      shortcut: 'r',
      hasSubTabs: false
    },
    {
      id: 'schedules',
      label: renderTabLabel(t('tabs.schedules'), 's'),
      shortcut: 's',
      hasSubTabs: false
    },
    {
      id: 'skills',
      label: renderTabLabel(t('tabs.skills'), 'k'),
      shortcut: 'k',
      hasSubTabs: false
    },
    {
      id: 'colors',
      label: renderTabLabel(t('tabs.colors', 'Colors'), 'l'),
      shortcut: 'l',
      hasSubTabs: false
    },
    {
      id: 'countries',
      label: renderTabLabel(t('tabs.countries', 'Countries'), 'n'),
      shortcut: 'n',
      hasSubTabs: false
    },
    {
      id: 'taskTypes',
      label: renderTabLabel(t('tabs.taskTypes', 'Task Types'), 't'),
      shortcut: 't',
      hasSubTabs: false
    }
  ];

  // Set up the toolbar callbacks. Layout effect: it runs before the children's effects, so a tab
  // opened directly (/admin?tab=schedules) finds window.set*Toolbar on its first render (#354).
  React.useLayoutEffect(() => {
    window.setSchedulesToolbar = setSchedulesToolbar;
    window.setDepartmentsToolbar = setDepartmentsToolbar;
    window.setSkillsToolbar = setSkillsToolbar;
    window.setRolesToolbar = setRolesToolbar;
    window.setColorsToolbar = setColorsToolbar;
    window.setCountriesToolbar = setCountriesToolbar;
    window.setTaskTypesToolbar = setTaskTypesToolbar;
    return () => {
      delete window.setSchedulesToolbar;
      delete window.setDepartmentsToolbar;
      delete window.setSkillsToolbar;
      delete window.setRolesToolbar;
      delete window.setColorsToolbar;
      delete window.setCountriesToolbar;
      delete window.setTaskTypesToolbar;
    };
  }, []);

  const renderContent = () => {
    const currentTab = tabs.find(t => t.id === activeTab);

    // Handle Overview tab
    if (activeTab === 'overview') {
      return (
        <div className="tab-content with-padding">
          <AdminOverview />
        </div>
      );
    }

    // Handle Users tab
    if (activeTab === 'users-roles') {
      return <Users />;
    }

    // Handle Departments tab
    if (activeTab === 'departments') {
      return <Departments />;
    }

    // Handle Roles tab
    if (activeTab === 'roles') {
      return <Roles />;
    }

    // Handle Schedules tab
    if (activeTab === 'schedules') {
      return (
        <div className="tab-content">
          <OperatorSchedules
            editMode={schedulesEditMode}
            onEditModeChange={setSchedulesEditMode}
          />
        </div>
      );
    }

    // Handle Skills tab
    if (activeTab === 'skills') {
      return (
        <div className="tab-content">
          <Skills />
        </div>
      );
    }

    // Handle Colors tab
    if (activeTab === 'colors') {
      return <Colors />;
    }

    // Handle Countries tab
    if (activeTab === 'countries') {
      return <Countries />;
    }

    // Handle Task Types tab
    if (activeTab === 'taskTypes') {
      return <TaskTypes />;
    }

    // Default content for other tabs
    return (
      <div className="tab-content">
        <div className="empty-state">
          <h3>{currentTab?.label}</h3>
          <p>{t('adminPage.underDevelopment')}</p>
          <p className="module-id">{t('adminPage.moduleId')}: {activeTab}</p>
        </div>
      </div>
    );
  };

  return (
    <BasePage
      title={t('title')}
      className="admin-page"
    >
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        toolbar={
          activeTab === 'schedules' ? schedulesToolbar :
          activeTab === 'departments' ? departmentsToolbar :
          activeTab === 'skills' ? skillsToolbar :
          activeTab === 'roles' ? rolesToolbar :
          activeTab === 'colors' ? colorsToolbar :
          activeTab === 'countries' ? countriesToolbar :
          activeTab === 'taskTypes' ? taskTypesToolbar :
          null
        }
      />

      {renderContent()}
    </BasePage>
  );
}

export default AdminPage;
